"use client";

import { CheckCircle } from "lucide-react";
import { DynamicIcon } from "@/components/ui/DynamicIcon";

interface AboutValuesProps {
    valuesData?: {
        title?: string;
        description?: string;
        values?: Array<{
            icon: string;
            title: string;
            description: string;
            features: string[];
        }>;
    };
}

export default function AboutValues({ valuesData }: AboutValuesProps) {
    // Eğer veri yoksa veya dizi boşsa alanı hiç render etme (Boşluk bırakmaz)
    if (!valuesData || !valuesData.values || valuesData.values.length === 0) {
        return null;
    }

    // Dinamik Başlık Parçalama (İlk kelimeyi renklendirmek için)
    // Örn: "Değerlerimiz ve İlkelerimiz" -> "Değerlerimiz" (Renkli), "ve İlkelerimiz" (Siyah)
    const title = valuesData.title || "Değerlerimiz ve İlkelerimiz";
    const titleWords = title.split(' ');
    const coloredWord = titleWords[0] || "";
    const restWords = titleWords.slice(1).join(' ');

    return (
        <section className="container mx-auto px-4 py-20 bg-white">

            {/* Üst Başlık Alanı */}
            <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                    <span style={{ color: 'var(--color-primary)' }}>{coloredWord}</span> {restWords}
                </h2>
                <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                    {valuesData.description || "Müşteri memnuniyetini her zaman ön planda tutan değerlerimizle hizmetinizdeyiz"}
                </p>
            </div>

            {/* Değerler Kartları Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {valuesData.values.map((value, index) => (
                    <div
                        key={index}
                        className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-2 group"
                    >
                        {/* İkon Kutusu */}
                        <div
                            className="rounded-xl w-12 h-12 flex items-center justify-center mb-6 transition-colors duration-300"
                            style={{
                                backgroundColor: 'color-mix(in srgb, var(--color-primary), transparent 90%)'
                            }}
                        >
                            {/* Panelden gelen string ikon adını (Örn: 'Heart') component'e çeviriyoruz */}
                            <DynamicIcon
                                name={value.icon || 'Heart'}
                                className="h-6 w-6"
                                style={{ color: 'var(--color-primary)' }}
                            />
                        </div>

                        <h3 className="text-xl font-semibold text-gray-900 mb-4">{value.title}</h3>
                        <p className="text-gray-600 mb-6 text-sm leading-relaxed">{value.description}</p>

                        <ul className="space-y-3">
                            {/* Özellikler listesini (Örn: "7/24 destek") döndürüyoruz */}
                            {value.features && value.features.map((feature, idx) => (
                                <li key={idx} className="flex items-center text-sm text-gray-500">
                                    <CheckCircle
                                        className="h-4 w-4 mr-2 shrink-0"
                                        style={{ color: 'var(--color-primary)' }}
                                    />
                                    {feature}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </section>
    );
}